import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type {
  ProjectId,
  ProjectMetadata,
  Version,
  VersionId,
} from '@/domain/project/types'

/**
 * IndexedDB schema per tech-spec §6.1.
 *
 * Stores:
 *   - projects        — keyPath `id`,        index on `updatedAt`
 *   - versions        — keyPath `id`,        indexes on `projectId`, `importedAt`
 *   - meta            — keyPath `key`        (locale, theme, telemetryConsent…)
 *   - projectSecrets  — keyPath `projectId`  (per-project HMAC key, ADR-026)
 *
 * dbVersion starts at 1; bump + migrate in `upgrade` for any future
 * schema change.
 */

export const MARKFY_DB_NAME = 'markfy'
export const MARKFY_DB_VERSION = 1

export type MetaKey = 'locale' | 'themePreference' | 'telemetryConsent'

export type MetaRow = {
  key: MetaKey
  value: unknown
}

export type ProjectSecretRow = {
  projectId: ProjectId
  /** 32 raw bytes (Uint8Array). Persisted as-is via structured clone. */
  key: Uint8Array
}

export interface MarkfyDb extends DBSchema {
  projects: {
    key: ProjectId
    value: ProjectMetadata
    indexes: { updatedAt: string }
  }
  versions: {
    key: VersionId
    value: Version
    indexes: { projectId: ProjectId; importedAt: string }
  }
  meta: {
    key: MetaKey
    value: MetaRow
  }
  projectSecrets: {
    key: ProjectId
    value: ProjectSecretRow
  }
}

export async function openMarkfyDb(
  name: string = MARKFY_DB_NAME,
): Promise<IDBPDatabase<MarkfyDb>> {
  return openDB<MarkfyDb>(name, MARKFY_DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('projects')) {
        const projects = db.createObjectStore('projects', { keyPath: 'id' })
        projects.createIndex('updatedAt', 'updatedAt')
      }
      if (!db.objectStoreNames.contains('versions')) {
        const versions = db.createObjectStore('versions', { keyPath: 'id' })
        versions.createIndex('projectId', 'projectId')
        versions.createIndex('importedAt', 'importedAt')
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' })
      }
      if (!db.objectStoreNames.contains('projectSecrets')) {
        db.createObjectStore('projectSecrets', { keyPath: 'projectId' })
      }
    },
  })
}

export async function deleteMarkfyDb(name: string = MARKFY_DB_NAME): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(name)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
    req.onblocked = () => resolve() // best-effort: continue even if other tabs hold connections
  })
}
